import { BufferAttribute, type BufferGeometry, type Mesh, type Object3D, Vector3 } from 'three';

/**
 * Wygładza cieniowanie modelu low-poly z zachowaniem ostrych krawędzi.
 * Dla każdego wierzchołka uśredniamy normalne ścianek, które go dzielą (po pozycji, nie po indeksie —
 * szwy UV nie przeszkadzają), ale tylko tych odchylonych o mniej niż `creaseDeg`.
 * Efekt: krągłe bryły (drzewa, kamienie) wyglądają miękko jak malowane, skrzynki zostają kanciaste.
 */
export function smoothShade(root: Object3D, creaseDeg = 75): void {
  const done = new WeakSet<BufferGeometry>();
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh || done.has(mesh.geometry)) return;
    done.add(mesh.geometry);
    smoothGeometry(mesh.geometry, Math.cos((creaseDeg * Math.PI) / 180));
  });
}

export function smoothGeometry(g: BufferGeometry, cosCrease: number): void {
  const pos = g.attributes.position as BufferAttribute | undefined;
  if (!pos) return;
  const index = g.index;
  const triCount = index ? index.count / 3 : pos.count / 3;
  const vi = (t: number, k: number) => (index ? index.getX(t * 3 + k) : t * 3 + k);

  // Normalne ścianek.
  const faceN: Vector3[] = [];
  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  for (let t = 0; t < triCount; t++) {
    a.fromBufferAttribute(pos, vi(t, 0));
    b.fromBufferAttribute(pos, vi(t, 1));
    c.fromBufferAttribute(pos, vi(t, 2));
    faceN.push(new Vector3().subVectors(c, b).cross(new Vector3().subVectors(a, b)).normalize());
  }

  // Ścianki przy każdej pozycji (zaokrąglonej, żeby skleić zdublowane wierzchołki na szwach).
  const key = (i: number) => `${pos.getX(i).toFixed(4)},${pos.getY(i).toFixed(4)},${pos.getZ(i).toFixed(4)}`;
  const facesAt = new Map<string, number[]>();
  for (let t = 0; t < triCount; t++) {
    for (let k = 0; k < 3; k++) {
      const kk = key(vi(t, k));
      const list = facesAt.get(kk);
      if (list) list.push(t);
      else facesAt.set(kk, [t]);
    }
  }

  // Normalna per wierzchołek-w-trójkącie; przy indeksowanej siatce bierzemy pierwszy trójkąt wierzchołka.
  const normals = new Float32Array(pos.count * 3);
  const assigned = new Uint8Array(pos.count);
  const n = new Vector3();
  for (let t = 0; t < triCount; t++) {
    for (let k = 0; k < 3; k++) {
      const v = vi(t, k);
      if (assigned[v]) continue;
      assigned[v] = 1;
      n.set(0, 0, 0);
      for (const other of facesAt.get(key(v))!) {
        if (faceN[other]!.dot(faceN[t]!) >= cosCrease) n.add(faceN[other]!);
      }
      n.normalize();
      normals.set([n.x, n.y, n.z], v * 3);
    }
  }
  g.setAttribute('normal', new BufferAttribute(normals, 3));
}
